import classNames from "classnames";
import React, { ReactNode } from "react";
import Button from "../button";
import ProgressIndicator from "../progress-indicator";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";
import {
  ViewMoreProps,
  ViewMoreStatus,
  ViewMoreButtonProps,
  ViewMoreLazyLoadProps,
} from "./view-more.types";

const renderViewMoreButtonTextDefault: ViewMoreProps["renderViewMoreButtonText"] =
  (count) => (count ? `View ${count} More Results` : "View More Results");

const renderLoadingTitleDefault: ViewMoreProps["renderLoadingTitle"] = (
  count
) => (count ? `Loading ${count} Results...` : "Loading Results...");

const renderLazyLoadingTitleDefault: ViewMoreProps["renderLazyLoadingTitle"] = (
  count
) => (count ? `Loading ${count} Results...` : "Loading Results...");

const renderNoResultsAvailableTextDefault: ViewMoreProps["renderNoResultsAvailableText"] =
  () => "No More Results";

/**
 * Function to render the loading content
 * inside the button. Internally called when isButton is true
 * @param loadingText ReactNode
 */
const renderLoadingForButton = (loadingText: ReactNode): React.ReactElement => (
  <>
    <progress></progress>
    <span className="spark-progress__meter"></span>
    {loadingText}
  </>
);

/**
 * Function to render the view more component
 * in button style. Called when isButton is true
 * @param props ViewMoreButtonProps
 */
const renderViewMoreButton = ({
  status,
  loadingRecordsCount,
  className,
  onClick,
  renderLoadingTitle = renderLoadingTitleDefault,
  renderViewMoreButtonText = renderViewMoreButtonTextDefault,
  ...rest
}: ViewMoreButtonProps): React.ReactElement => {
  const viewMoreBtnClasses = classNames(
    "spark-btn--view-more",
    { "spark-progress active": status === ViewMoreStatus.LOADING },
    className
  );

  return (
    <Button
      size="md"
      onClick={onClick}
      className={viewMoreBtnClasses}
      {...rest}
    >
      {status === ViewMoreStatus.LOADING
        ? renderLoadingForButton(renderLoadingTitle(loadingRecordsCount))
        : renderViewMoreButtonText(loadingRecordsCount)}
    </Button>
  );
};

/**
 * Function to render the view more component
 * with a spinny and text and only text when no results
 * Called when isButton is false
 * @param props ViewMoreLazyLoadProps
 */
const renderLazyLoadContent = ({
  status,
  loadingRecordsCount,
  renderLazyLoadingTitle = renderLazyLoadingTitleDefault,
  renderNoResultsAvailableText = renderNoResultsAvailableTextDefault,
  className,
}: ViewMoreLazyLoadProps): React.ReactElement | null => {
  if (status === ViewMoreStatus.NONE) {
    return null;
  }

  const lazyLoadClasses = classNames(
    "spark-progress--lazy-load",
    { "spark-progress--disabled": status === ViewMoreStatus.NO_RESULTS },
    className
  );

  let progressText = "";

  if (status === ViewMoreStatus.LOADING) {
    progressText = renderLazyLoadingTitle(loadingRecordsCount);
  } else if (status === ViewMoreStatus.NO_RESULTS) {
    progressText = renderNoResultsAvailableText();
  }
  return (
    <ProgressIndicator
      progressText={progressText}
      size="xs"
      type="indeterminate"
      className={lazyLoadClasses}
    />
  );
};

export const ViewMore = ({
  isButton = true,
  renderLoadingTitle = renderLoadingTitleDefault,
  renderLazyLoadingTitle = renderLazyLoadingTitleDefault,
  loadingRecordsCount,
  renderViewMoreButtonText = renderViewMoreButtonTextDefault,
  className,
  onClick,
  renderNoResultsAvailableText =renderNoResultsAvailableTextDefault,
  status,
  ...rest
}: ViewMoreProps): React.ReactElement | null => {
  let statusValue = status;
  if (!Object.values(ViewMoreStatus).includes(statusValue)) {
    statusValue = ViewMoreStatus.NONE;
  }

  useSSR(() => {
    if (isButton) {
      isPropEmpty(rest.id) && showSSRHydrationWarning("id", "ViewMore");
    }
  });

  if (isButton) {
    return renderViewMoreButton({
      renderViewMoreButtonText,
      status: statusValue,
      renderLoadingTitle,
      loadingRecordsCount,
      className,
      onClick,
      ...rest,
    });
  } else {
    return renderLazyLoadContent({
      status: statusValue,
      renderLazyLoadingTitle,
      loadingRecordsCount,
      renderNoResultsAvailableText,
      className,
    });
  }
};

ViewMore.displayName = "ViewMore";
